import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';
import api from '../api/client';

const ChangePassword = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const newPassword = watch('newPassword', '');

  // Password validation checks
  const hasMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await api.post('/auth/change-password', {
        current_password: data.currentPassword,
        new_password: data.newPassword
      });

      // Changing the password revokes every token issued before now, including the
      // one we just used. The backend returns a replacement so this device stays
      // signed in; without storing it the very next request would 401.
      if (res.data?.access_token) {
        localStorage.setItem('token', res.data.access_token);
      }

      setSuccess(t('passwordChangedSuccess') || 'Password changed successfully!');

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      console.error('Change password error:', err);
      setError(err.response?.data?.detail || t('passwordChangeError') || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Redirect if not logged in
  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      p={2}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          backgroundColor: colors.grey[800]
        }}
      >
        <Typography variant="h3" fontWeight="bold" mb={3} textAlign="center">
          {t('changePassword') || 'Change Password'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label={t('currentPassword') || 'Current Password'}
            type="password"
            margin="normal"
            {...register('currentPassword', {
              required: t('currentPasswordRequired') || 'Current password is required'
            })}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword?.message}
            disabled={loading}
          />

          <TextField
            fullWidth
            label={t('newPassword') || 'New Password'}
            type="password"
            margin="normal"
            {...register('newPassword', {
              required: t('newPasswordRequired') || 'New password is required',
              validate: {
                minLength: (v) => v.length >= 8 || t('passwordMinLength') || 'Password must be at least 8 characters',
                hasUppercase: (v) => /[A-Z]/.test(v) || t('passwordNeedsUppercase') || 'Password must contain at least one uppercase letter',
                hasLowercase: (v) => /[a-z]/.test(v) || t('passwordNeedsLowercase') || 'Password must contain at least one lowercase letter',
                hasNumber: (v) => /\d/.test(v) || t('passwordNeedsNumber') || 'Password must contain at least one number'
              }
            })}
            error={!!errors.newPassword}
            helperText={errors.newPassword?.message}
            disabled={loading}
          />

          <TextField
            fullWidth
            label={t('confirmNewPassword') || 'Confirm New Password'}
            type="password"
            margin="normal"
            {...register('confirmPassword', {
              required: t('confirmPasswordRequired') || 'Please confirm your password',
              validate: (value) =>
                value === newPassword || t('passwordsDoNotMatch') || 'Passwords do not match'
            })}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            disabled={loading}
          />

          {/* Password requirements checklist */}
          <Box mt={2} mb={2}>
            <Typography variant="body2" color={colors.grey[100]} mb={1}>
              {t('passwordRequirements') || 'Password Requirements'}:
            </Typography>
            <List dense>
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {hasMinLength ?
                    <CheckCircleIcon color="success" fontSize="small" /> :
                    <CancelIcon color="error" fontSize="small" />
                  }
                </ListItemIcon>
                <ListItemText
                  primary={t('atLeast8Characters') || 'At least 8 characters'}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {hasUppercase ?
                    <CheckCircleIcon color="success" fontSize="small" /> :
                    <CancelIcon color="error" fontSize="small" />
                  }
                </ListItemIcon>
                <ListItemText
                  primary={t('oneUppercaseLetter') || 'One uppercase letter'}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {hasLowercase ?
                    <CheckCircleIcon color="success" fontSize="small" /> :
                    <CancelIcon color="error" fontSize="small" />
                  }
                </ListItemIcon>
                <ListItemText
                  primary={t('oneLowercaseLetter') || 'One lowercase letter'}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {hasNumber ?
                    <CheckCircleIcon color="success" fontSize="small" /> :
                    <CancelIcon color="error" fontSize="small" />
                  }
                </ListItemIcon>
                <ListItemText
                  primary={t('oneNumber') || 'One number'}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </Box>

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {loading ? t('changingPassword') || 'Changing Password...' : t('changePassword') || 'Change Password'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate('/')}
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default ChangePassword;
